/**
 * Sales Instruction Property Overview Page
 * ISOLATED MODULE - Uses /api/sales-instructions/* endpoints only
 * Route: /sales/instructions/:instructionId
 * 
 * Tabs: Dashboard | Property | Contract | Marketing | Photos | Floorplan | EPC | Documents | Brochure | Publish
 */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Home, MapPin, Bed, Bath, Square, PoundSterling,
  FileText, Image, Layout, Zap, FolderOpen, BookOpen, Globe,
  Loader2, CheckCircle, Clock, AlertCircle, XCircle,
  Upload, Trash2, Eye, Send, Download, RefreshCw, Save,
  User, Mail, Phone, Building2, Check, X, Users, Plus,
  Calendar, MessageSquare, Star, ThumbsUp, ThumbsDown, Banknote
} from "lucide-react";
import { API } from "@/App";

// Status colors
const statusColors = {
  DRAFT: "bg-slate-100 text-slate-700 border-slate-200",
  READY_TO_APPROVE: "bg-amber-100 text-amber-700 border-amber-200",
  APPROVED_TO_GO_LIVE: "bg-blue-100 text-blue-700 border-blue-200",
  LIVE: "bg-green-100 text-green-700 border-green-200",
  WITHDRAWN: "bg-red-100 text-red-700 border-red-200"
};

const contractStatusColors = {
  NOT_SENT: "bg-slate-100 text-slate-600",
  SENT: "bg-amber-100 text-amber-600",
  VIEWED: "bg-blue-100 text-blue-600",
  SIGNED: "bg-green-100 text-green-600",
  DECLINED: "bg-red-100 text-red-600"
};

const brochureStatusColors = {
  DRAFT: "bg-slate-100 text-slate-600",
  GENERATED: "bg-blue-100 text-blue-600",
  SENT_TO_SELLER: "bg-amber-100 text-amber-600",
  CHANGES_REQUESTED: "bg-orange-100 text-orange-600",
  APPROVED: "bg-green-100 text-green-600",
  OVERRIDE_APPROVED: "bg-green-100 text-green-600"
};

const SIPropertyOverviewPage = () => {
  const { instructionId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Data states
  const [instruction, setInstruction] = useState(null);
  const [media, setMedia] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [contract, setContract] = useState(null);
  const [brochure, setBrochure] = useState(null);
  const [parties, setParties] = useState([]);
  const [readiness, setReadiness] = useState(null);
  
  // DocuSign states
  const [docusignAvailable, setDocusignAvailable] = useState(false);
  const [docusignStatus, setDocusignStatus] = useState(null);
  const [showDocusignModal, setShowDocusignModal] = useState(false);
  const [docusignSigners, setDocusignSigners] = useState([]);
  const [sendingDocusign, setSendingDocusign] = useState(false);

  // Viewings & Feedback states
  const [viewings, setViewings] = useState([]);
  const [viewingsStats, setViewingsStats] = useState({});
  const [feedbackList, setFeedbackList] = useState([]);
  const [showAddViewingModal, setShowAddViewingModal] = useState(false);
  const [showSendFeedbackModal, setShowSendFeedbackModal] = useState(false);
  const [newViewing, setNewViewing] = useState({
    applicant_name: "",
    applicant_email: "",
    applicant_phone: "",
    viewing_date: "",
    viewing_time: "",
    notes: ""
  });
  const [newFeedback, setNewFeedback] = useState({
    subject: "Viewing Feedback",
    message: ""
  });
  const [savingViewing, setSavingViewing] = useState(false);
  const [savingFeedback, setSavingFeedback] = useState(false);

  // Offers states
  const [offers, setOffers] = useState([]);
  const [offersStats, setOffersStats] = useState({});
  const [showAddOfferModal, setShowAddOfferModal] = useState(false);
  const [newOffer, setNewOffer] = useState({
    amount: "",
    buyer_name: "",
    buyer_email: "",
    buyer_phone: "",
    buyer_position: "First Time Buyer",
    conditions: "",
    notes: "",
    mortgage_in_principle: false,
    proof_of_funds: false
  });
  const [savingOffer, setSavingOffer] = useState(false);

  // Upload refs
  const photoInputRef = useRef(null);
  const floorplanInputRef = useRef(null);
  const epcInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const contractInputRef = useRef(null);

  // Form state for editing
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);
  const [documentCategory, setDocumentCategory] = useState("OTHER");

  useEffect(() => {
    fetchInstructionData();
    checkDocusignStatus();
    fetchViewings();
    fetchFeedback();
    fetchOffers();
  }, [instructionId]);
  
  const checkDocusignStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/sales-instructions/docusign/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDocusignAvailable(response.data.configured);
    } catch (error) {
      setDocusignAvailable(false);
    }
  };
  
  const fetchDocusignContractStatus = async () => {
    if (!contract?.docusign_envelope_id) return;
    try {
      const response = await axios.get(
        `${API}/sales-instructions/instructions/${instructionId}/contract/docusign-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDocusignStatus(response.data);
    } catch (error) {
      console.error("Error fetching DocuSign status:", error);
    }
  };

  // Fetch viewings for this instruction
  const fetchViewings = async () => {
    try {
      const response = await axios.get(
        `${API}/sales-instructions/instructions/${instructionId}/viewings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setViewings(response.data.viewings || []);
      setViewingsStats(response.data.stats || {});
    } catch (error) {
      console.error("Error fetching viewings:", error);
    }
  };

  // Fetch feedback for this instruction
  const fetchFeedback = async () => {
    try {
      const response = await axios.get(
        `${API}/sales-instructions/instructions/${instructionId}/feedback`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeedbackList(response.data.feedback || []);
    } catch (error) {
      console.error("Error fetching feedback:", error);
    }
  };

  // Create a new viewing
  const handleCreateViewing = async () => {
    if (!newViewing.applicant_name || !newViewing.viewing_date || !newViewing.viewing_time) {
      toast.error("Please fill in required fields");
      return;
    }
    setSavingViewing(true);
    try {
      await axios.post(
        `${API}/sales-instructions/instructions/${instructionId}/viewings`,
        { ...newViewing, instruction_id: instructionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Viewing created");
      setShowAddViewingModal(false);
      setNewViewing({ applicant_name: "", applicant_email: "", applicant_phone: "", viewing_date: "", viewing_time: "", notes: "" });
      fetchViewings();
    } catch (error) {
      toast.error("Failed to create viewing");
    } finally {
      setSavingViewing(false);
    }
  };

  // Update viewing status
  const handleUpdateViewingStatus = async (viewingId, status) => {
    try {
      await axios.patch(
        `${API}/sales-instructions/viewings/${viewingId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Viewing updated");
      fetchViewings();
    } catch (error) {
      toast.error("Failed to update viewing");
    }
  };

  // Add feedback to viewing
  const handleAddViewingFeedback = async (viewingId, feedback, rating) => {
    try {
      await axios.patch(
        `${API}/sales-instructions/viewings/${viewingId}`,
        { feedback, feedback_rating: rating },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Feedback added");
      fetchViewings();
    } catch (error) {
      toast.error("Failed to add feedback");
    }
  };

  // Send feedback to seller
  const handleSendFeedback = async () => {
    if (!newFeedback.message) {
      toast.error("Please enter a message");
      return;
    }
    setSavingFeedback(true);
    try {
      await axios.post(
        `${API}/sales-instructions/instructions/${instructionId}/feedback`,
        { ...newFeedback, instruction_id: instructionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Feedback sent to seller");
      setShowSendFeedbackModal(false);
      setNewFeedback({ subject: "Viewing Feedback", message: "" });
      fetchFeedback();
    } catch (error) {
      toast.error("Failed to send feedback");
    } finally {
      setSavingFeedback(false);
    }
  };

  // Fetch offers for this instruction
  const fetchOffers = async () => {
    try {
      const response = await axios.get(
        `${API}/sales-instructions/instructions/${instructionId}/offers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOffers(response.data.offers || []);
      setOffersStats(response.data.stats || {});
    } catch (error) {
      console.error("Error fetching offers:", error);
    }
  };

  // Create a new offer
  const handleCreateOffer = async () => {
    if (!newOffer.buyer_name || !newOffer.amount) {
      toast.error("Please fill in buyer name and offer amount");
      return;
    }
    setSavingOffer(true);
    try {
      await axios.post(
        `${API}/sales-instructions/instructions/${instructionId}/offers`,
        { ...newOffer, instruction_id: instructionId, amount: parseInt(newOffer.amount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Offer created and seller notified");
      setShowAddOfferModal(false);
      setNewOffer({ amount: "", buyer_name: "", buyer_email: "", buyer_phone: "", buyer_position: "First Time Buyer", conditions: "", notes: "", mortgage_in_principle: false, proof_of_funds: false });
      fetchOffers();
    } catch (error) {
      toast.error("Failed to create offer");
    } finally {
      setSavingOffer(false);
    }
  };

  // Update offer status
  const handleUpdateOfferStatus = async (offerId, status) => {
    try {
      await axios.patch(
        `${API}/sales-instructions/offers/${offerId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Offer ${status}`);
      fetchOffers();
    } catch (error) {
      toast.error("Failed to update offer");
    }
  };

  const fetchInstructionData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/sales-instructions/instructions/${instructionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      setInstruction(data.instruction);
      setMedia(data.media || []);
      setDocuments(data.documents || []);
      setContract(data.contract);
      setBrochure(data.brochure);
      setParties(data.parties || []);
      setReadiness(data.readiness);
      setFormData(data.instruction || {});
    } catch (error) {
      console.error("Error fetching instruction:", error);
      toast.error("Failed to load instruction");
      navigate("/sales/instructions/list");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.patch(
        `${API}/sales-instructions/instructions/${instructionId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Changes saved");
      setEditMode(false);
      fetchInstructionData();
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleMediaUpload = async (file, mediaType) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("media_type", mediaType);
      formData.append("sort_order", media.length);

      await axios.post(
        `${API}/sales-instructions/instructions/${instructionId}/media`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      
      toast.success(`${mediaType.replace("_", " ")} uploaded`);
      fetchInstructionData();
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Bulk upload handler for multiple photos (up to 20)
  const handleBulkMediaUpload = async (files, mediaType = "PHOTO") => {
    if (files.length === 0) return;
    
    if (files.length > 20) {
      toast.error("Maximum 20 files can be uploaded at once");
      return;
    }
    
    try {
      setUploading(true);
      const formData = new FormData();
      
      // Append all files
      for (let i = 0; i < files.length; i++) {
        formData.append("files", files[i]);
      }
      formData.append("media_type", mediaType);

      const response = await axios.post(
        `${API}/sales-instructions/instructions/${instructionId}/media/bulk`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      
      const { uploaded_count, error_count, message } = response.data;
      
      if (uploaded_count > 0) {
        toast.success(message);
      }
      if (error_count > 0) {
        toast.error(`${error_count} file(s) failed to upload`);
      }
      
      fetchInstructionData();
    } catch (error) {
      toast.error("Bulk upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentUpload = async (file) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", documentCategory);
      formData.append("title", file.name);

      await axios.post(
        `${API}/sales-instructions/instructions/${instructionId}/documents`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Document uploaded");
      fetchInstructionData();
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm("Delete this media?")) return;
    
    try {
      await axios.delete(
        `${API}/sales-instructions/media/${mediaId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Media deleted");
      fetchInstructionData();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    
    try {
      await axios.delete(
        `${API}/sales-instructions/documents/${docId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Document deleted");
      fetchInstructionData();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const handleSendContract = async () => {
    try {
      await axios.post(
        `${API}/sales-instructions/instructions/${instructionId}/contract/send`,
        { send_to_all_sellers: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Contract marked as sent");
      fetchInstructionData();
    } catch (error) {
      toast.error("Failed to send contract");
    }
  };
  
  const handleSendDocuSign = async () => {
    if (docusignSigners.length === 0) {
      toast.error("Add at least one seller signer");
      return;
    }
    
    try {
      setSendingDocusign(true);
      const response = await axios.post(
        `${API}/sales-instructions/instructions/${instructionId}/contract/send-docusign`,
        { signers: docusignSigners },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || "Contract sent via DocuSign!");
      setShowDocusignModal(false);
      setDocusignSigners([]);
      fetchInstructionData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send via DocuSign");
    } finally {
      setSendingDocusign(false);
    }
  };
  
  const handleResendDocuSign = async () => {
    try {
      await axios.post(
        `${API}/sales-instructions/instructions/${instructionId}/contract/resend-docusign`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("DocuSign notifications resent");
    } catch (error) {
      toast.error("Failed to resend notifications");
    }
  };
  
  const handleVoidDocuSign = async () => {
    if (!confirm("Are you sure you want to void this contract? This cannot be undone.")) return;
    
    try {
      await axios.post(
        `${API}/sales-instructions/instructions/${instructionId}/contract/void-docusign`,
        { reason: "Cancelled by staff" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("DocuSign contract voided");
      fetchInstructionData();
    } catch (error) {
      toast.error("Failed to void contract");
    }
  };
  
  const initDocusignSigners = () => {
    // Pre-populate with existing sellers
    const sellers = parties.filter(p => p.party_type === "SELLER" || p.party_type === "CO_SELLER");
    setDocusignSigners(sellers.map(s => ({ name: s.name, email: s.email })));
    setShowDocusignModal(true);
  };
  
  const addDocusignSigner = () => {
    setDocusignSigners([...docusignSigners, { name: "", email: "" }]);
  };
  
  const removeDocusignSigner = (index) => {
    setDocusignSigners(docusignSigners.filter((_, i) => i !== index));
  };
  
  const updateDocusignSigner = (index, field, value) => {
    const updated = [...docusignSigners];
    updated[index][field] = value;
    setDocusignSigners(updated);
  };

  const handleUploadSignedContract = async (file) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("notes", "Signed contract uploaded");

      await axios.post(
        `${API}/sales-instructions/instructions/${instructionId}/contract/upload-signed`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Signed contract uploaded - Status: SIGNED");
      fetchInstructionData();
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateBrochure = async () => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("template_version", "GR_A4_BW_V1");

      const response = await axios.post(
        `${API}/sales-instructions/instructions/${instructionId}/brochure/generate`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Brochure generated");
      fetchInstructionData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Brochure generation failed");
    } finally {
      setUploading(false);
    }
  };

  const handleBrochureAction = async (action) => {
    try {
      const endpoint = {
        send: `/sales-instructions/instructions/${instructionId}/brochure/send-to-seller`,
        approve: `/sales-instructions/brochure/${brochure?.id}/agent-override-approve`
      }[action];

      await axios.post(
        `${API}${endpoint}`,
        { notes: `Action: ${action}` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Brochure ${action} successful`);
      fetchInstructionData();
    } catch (error) {
      toast.error(`Brochure ${action} failed`);
    }
  };

  const handlePublish = async () => {
    if (!readiness?.is_ready) {
      toast.error("Instruction not ready to publish. Check readiness checklist.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("targets", JSON.stringify(["RIGHTMOVE", "ZOOPLA", "ONTHEMARKET"]));

      await axios.post(
        `${API}/sales-instructions/instructions/${instructionId}/publish`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Property queued for publishing");
      fetchInstructionData();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || "Publish failed");
    }
  };

  // Helper functions
  const formatPrice = (amount) => amount ? `£${amount.toLocaleString()}` : "—";
  
  const mainImage = media.find(m => m.type === "MAIN_IMAGE");
  const photos = media.filter(m => m.type === "PHOTO" || m.type === "MAIN_IMAGE");
  const floorplan = media.find(m => m.type === "FLOORPLAN");
  const epc = media.find(m => m.type === "EPC");

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: Home },
    { id: "property", label: "Property", icon: Building2 },
    { id: "viewings", label: "Viewings", icon: Calendar },
    { id: "offers", label: "Offers", icon: Banknote },
    { id: "feedback", label: "Feedback", icon: MessageSquare },
    { id: "contract", label: "Contract", icon: FileText },
    { id: "marketing", label: "Marketing", icon: Layout },
    { id: "photos", label: "Photos", icon: Image },
    { id: "floorplan", label: "Floorplan", icon: Layout },
    { id: "epc", label: "EPC", icon: Zap },
    { id: "documents", label: "Documents", icon: FolderOpen },
    { id: "brochure", label: "Brochure", icon: BookOpen },
    { id: "publish", label: "Publish", icon: Globe }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!instruction) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Instruction not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/sales/instructions/list")}
                className="text-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-slate-900">
                    {instruction.address_line_1}
                  </h1>
                  <Badge className={statusColors[instruction.status]}>
                    {instruction.status.replace(/_/g, " ")}
                  </Badge>
                </div>
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {instruction.town}, {instruction.postcode}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-2xl font-bold text-slate-900">
                  {formatPrice(instruction.price_amount)}
                </div>
                <div className="text-sm text-slate-500">
                  {instruction.price_type?.replace(/_/g, " ")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* Sidebar Navigation */}
          <div className="w-56 flex-shrink-0">
            <Card>
              <CardContent className="p-2">
                <nav className="space-y-1">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeTab === tab.id
                            ? "bg-blue-50 text-blue-700"
                            : "text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>

            {/* Readiness Checklist */}
            {readiness && (
              <Card className="mt-4">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm">Readiness</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 space-y-2">
                  {Object.entries(readiness.checklist || {}).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2 text-xs">
                      {value === true || (typeof value === "number" && value > 0) ? (
                        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-red-400" />
                      )}
                      <span className="text-slate-600">
                        {key.replace(/_/g, " ")}
                      </span>
                    </div>
                  ))}
                  <div className="pt-2 border-t mt-2">
                    <Badge className={readiness.is_ready ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                      {readiness.is_ready ? "Ready to Publish" : "Not Ready"}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Dashboard Tab */}
            {activeTab === "dashboard" && (
              <div className="space-y-6">
                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-slate-500">Property</div>
                      <div className="text-lg font-semibold">{instruction.property_type || "—"}</div>
                      <div className="text-xs text-slate-400">
                        {instruction.bedrooms || 0} bed • {instruction.bathrooms || 0} bath
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-slate-500">Contract</div>
                      <Badge className={contractStatusColors[contract?.status]}>
                        {contract?.status || "—"}
                      </Badge>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-slate-500">Brochure</div>
                      <Badge className={brochureStatusColors[brochure?.status] || "bg-slate-100"}>
                        {brochure?.status || "Not Generated"}
                      </Badge>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-slate-500">Photos</div>
                      <div className="text-lg font-semibold">{photos.length}</div>
                      <div className="text-xs text-slate-400">uploaded</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Main Image Preview */}
                {mainImage && (
                  <Card>
                    <CardContent className="p-0">
                      <img
                        src={`${API.replace("/api", "")}${mainImage.file_url}`}
                        alt="Main"
                        className="w-full h-64 object-cover rounded-lg"
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Sellers */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Sellers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {parties.length === 0 ? (
                      <p className="text-sm text-slate-500">No sellers added</p>
                    ) : (
                      <div className="space-y-3">
                        {parties.map((party) => (
                          <div key={party.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                            <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-slate-500" />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium">{party.name}</div>
                              <div className="text-sm text-slate-500">{party.email}</div>
                            </div>
                            <Badge variant="outline">{party.party_type}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Property Tab */}
            {activeTab === "property" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Property Details</CardTitle>
                  <Button
                    variant={editMode ? "default" : "outline"}
                    size="sm"
                    onClick={() => editMode ? handleSave() : setEditMode(true)}
                    disabled={saving}
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : editMode ? (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </>
                    ) : (
                      "Edit"
                    )}
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Address</Label>
                      {editMode ? (
                        <Input
                          value={formData.address_line_1 || ""}
                          onChange={(e) => setFormData({...formData, address_line_1: e.target.value})}
                        />
                      ) : (
                        <p className="text-sm mt-1">{instruction.address_line_1}</p>
                      )}
                    </div>
                    <div>
                      <Label>Town</Label>
                      {editMode ? (
                        <Input
                          value={formData.town || ""}
                          onChange={(e) => setFormData({...formData, town: e.target.value})}
                        />
                      ) : (
                        <p className="text-sm mt-1">{instruction.town}</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Property Type</Label>
                      <p className="text-sm mt-1">{instruction.property_type || "—"}</p>
                    </div>
                    <div>
                      <Label>Bedrooms</Label>
                      <p className="text-sm mt-1">{instruction.bedrooms || "—"}</p>
                    </div>
                    <div>
                      <Label>Bathrooms</Label>
                      <p className="text-sm mt-1">{instruction.bathrooms || "—"}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tenure</Label>
                      <p className="text-sm mt-1">{instruction.tenure}</p>
                    </div>
                    <div>
                      <Label>Size</Label>
                      <p className="text-sm mt-1">{instruction.size || "—"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Viewings Tab */}
            {activeTab === "viewings" && (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-blue-600" />
                          Property Viewings
                        </CardTitle>
                        <CardDescription>Manage viewings and collect feedback</CardDescription>
                      </div>
                      <Button onClick={() => setShowAddViewingModal(true)} data-testid="add-viewing-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Viewing
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="bg-slate-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-slate-800">{viewingsStats.total || 0}</p>
                        <p className="text-xs text-slate-500">Total</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-amber-600">{viewingsStats.pending || 0}</p>
                        <p className="text-xs text-amber-600">Pending</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-blue-600">{viewingsStats.confirmed || 0}</p>
                        <p className="text-xs text-blue-600">Confirmed</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-green-600">{viewingsStats.completed || 0}</p>
                        <p className="text-xs text-green-600">Completed</p>
                      </div>
                    </div>

                    {/* Viewings List */}
                    {viewings.length === 0 ? (
                      <div className="text-center py-12 bg-slate-50 rounded-lg">
                        <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No viewings scheduled yet</p>
                        <Button 
                          variant="outline" 
                          className="mt-4"
                          onClick={() => setShowAddViewingModal(true)}
                        >
                          Schedule First Viewing
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {viewings.map((viewing) => (
                          <div 
                            key={viewing.id} 
                            className="border rounded-lg p-4 hover:shadow-sm transition-shadow"
                            data-testid={`viewing-${viewing.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <p className="font-semibold text-slate-800">{viewing.applicant_name}</p>
                                  <Badge className={
                                    viewing.status === "completed" ? "bg-green-100 text-green-700" :
                                    viewing.status === "confirmed" ? "bg-blue-100 text-blue-700" :
                                    viewing.status === "pending" ? "bg-amber-100 text-amber-700" :
                                    viewing.status === "cancelled" ? "bg-red-100 text-red-700" :
                                    "bg-slate-100 text-slate-700"
                                  }>
                                    {viewing.status}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {viewing.viewing_date}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {viewing.viewing_time}
                                  </span>
                                  {viewing.applicant_email && (
                                    <span className="flex items-center gap-1">
                                      <Mail className="w-4 h-4" />
                                      {viewing.applicant_email}
                                    </span>
                                  )}
                                  {viewing.applicant_phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-4 h-4" />
                                      {viewing.applicant_phone}
                                    </span>
                                  )}
                                </div>
                                {viewing.notes && (
                                  <p className="text-sm text-slate-600 mt-2 italic">"{viewing.notes}"</p>
                                )}
                                
                                {/* Feedback Section */}
                                {viewing.feedback && (
                                  <div className="mt-3 p-3 bg-emerald-50 rounded-lg border-l-4 border-emerald-500">
                                    <div className="flex items-center gap-2 mb-1">
                                      <MessageSquare className="w-4 h-4 text-emerald-600" />
                                      <span className="font-medium text-emerald-700 text-sm">Feedback</span>
                                      {viewing.feedback_rating && (
                                        <div className="flex items-center gap-0.5 ml-auto">
                                          {[1,2,3,4,5].map((star) => (
                                            <Star 
                                              key={star}
                                              className={`w-4 h-4 ${star <= viewing.feedback_rating ? "text-yellow-500 fill-yellow-500" : "text-slate-300"}`}
                                            />
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <p className="text-sm text-slate-600">{viewing.feedback}</p>
                                  </div>
                                )}
                              </div>
                              
                              {/* Actions */}
                              <div className="flex items-center gap-2 ml-4">
                                {viewing.status === "pending" && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleUpdateViewingStatus(viewing.id, "confirmed")}
                                    >
                                      <Check className="w-4 h-4 mr-1" />
                                      Confirm
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="text-red-600 hover:text-red-700"
                                      onClick={() => handleUpdateViewingStatus(viewing.id, "cancelled")}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                                {viewing.status === "confirmed" && (
                                  <Button 
                                    size="sm"
                                    onClick={() => handleUpdateViewingStatus(viewing.id, "completed")}
                                  >
                                    Mark Complete
                                  </Button>
                                )}
                                {viewing.status === "completed" && !viewing.feedback && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      const feedback = prompt("Enter viewing feedback:");
                                      const rating = prompt("Rating (1-5):");
                                      if (feedback) {
                                        handleAddViewingFeedback(viewing.id, feedback, parseInt(rating) || null);
                                      }
                                    }}
                                  >
                                    <MessageSquare className="w-4 h-4 mr-1" />
                                    Add Feedback
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Add Viewing Modal */}
                {showAddViewingModal && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <Card className="w-full max-w-md">
                      <CardHeader>
                        <CardTitle>Schedule Viewing</CardTitle>
                        <CardDescription>Add a new viewing for this property</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label>Applicant Name *</Label>
                          <Input
                            value={newViewing.applicant_name}
                            onChange={(e) => setNewViewing({...newViewing, applicant_name: e.target.value})}
                            placeholder="John Smith"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Email</Label>
                            <Input
                              type="email"
                              value={newViewing.applicant_email}
                              onChange={(e) => setNewViewing({...newViewing, applicant_email: e.target.value})}
                              placeholder="john@example.com"
                            />
                          </div>
                          <div>
                            <Label>Phone</Label>
                            <Input
                              value={newViewing.applicant_phone}
                              onChange={(e) => setNewViewing({...newViewing, applicant_phone: e.target.value})}
                              placeholder="07123456789"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Date *</Label>
                            <Input
                              type="date"
                              value={newViewing.viewing_date}
                              onChange={(e) => setNewViewing({...newViewing, viewing_date: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Time *</Label>
                            <Input
                              type="time"
                              value={newViewing.viewing_time}
                              onChange={(e) => setNewViewing({...newViewing, viewing_time: e.target.value})}
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Notes</Label>
                          <Textarea
                            value={newViewing.notes}
                            onChange={(e) => setNewViewing({...newViewing, notes: e.target.value})}
                            placeholder="Any special instructions..."
                            rows={3}
                          />
                        </div>
                      </CardContent>
                      <div className="flex justify-end gap-2 p-4 border-t">
                        <Button variant="outline" onClick={() => setShowAddViewingModal(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleCreateViewing} disabled={savingViewing}>
                          {savingViewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Schedule Viewing
                        </Button>
                      </div>
                    </Card>
                  </div>
                )}
              </div>
            )}

            {/* Feedback Tab */}
            {activeTab === "feedback" && (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <MessageSquare className="w-5 h-5 text-emerald-600" />
                          Seller Feedback
                        </CardTitle>
                        <CardDescription>Send updates and feedback to the seller</CardDescription>
                      </div>
                      <Button onClick={() => setShowSendFeedbackModal(true)} data-testid="send-feedback-btn">
                        <Send className="w-4 h-4 mr-2" />
                        Send Feedback
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {feedbackList.length === 0 ? (
                      <div className="text-center py-12 bg-slate-50 rounded-lg">
                        <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No feedback sent yet</p>
                        <Button 
                          variant="outline" 
                          className="mt-4"
                          onClick={() => setShowSendFeedbackModal(true)}
                        >
                          Send First Update
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {feedbackList.map((fb) => (
                          <div 
                            key={fb.id} 
                            className="border rounded-lg p-4"
                            data-testid={`feedback-${fb.id}`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Badge className={
                                  fb.subject === "Viewing Feedback" ? "bg-blue-100 text-blue-700" :
                                  fb.subject === "Weekly Update" ? "bg-emerald-100 text-emerald-700" :
                                  fb.subject === "Price Review" ? "bg-purple-100 text-purple-700" :
                                  "bg-slate-100 text-slate-700"
                                }>
                                  {fb.subject}
                                </Badge>
                                {fb.read && (
                                  <span className="text-xs text-green-600 flex items-center gap-1">
                                    <Eye className="w-3 h-3" /> Read
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-slate-400">
                                {new Date(fb.created_at).toLocaleDateString('en-GB', {
                                  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{fb.message}</p>
                            <p className="text-xs text-slate-400 mt-2">Sent by: {fb.sender_name}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Send Feedback Modal */}
                {showSendFeedbackModal && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <Card className="w-full max-w-lg">
                      <CardHeader>
                        <CardTitle>Send Feedback to Seller</CardTitle>
                        <CardDescription>This will be visible in the seller&apos;s portal</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label>Subject</Label>
                          <Select 
                            value={newFeedback.subject} 
                            onValueChange={(val) => setNewFeedback({...newFeedback, subject: val})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Viewing Feedback">Viewing Feedback</SelectItem>
                              <SelectItem value="Weekly Update">Weekly Update</SelectItem>
                              <SelectItem value="General Update">General Update</SelectItem>
                              <SelectItem value="Price Review">Price Review</SelectItem>
                              <SelectItem value="Marketing Update">Marketing Update</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Message *</Label>
                          <Textarea
                            value={newFeedback.message}
                            onChange={(e) => setNewFeedback({...newFeedback, message: e.target.value})}
                            placeholder="Enter your feedback message..."
                            rows={6}
                          />
                        </div>
                      </CardContent>
                      <div className="flex justify-end gap-2 p-4 border-t">
                        <Button variant="outline" onClick={() => setShowSendFeedbackModal(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleSendFeedback} disabled={savingFeedback}>
                          {savingFeedback ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                          Send to Seller
                        </Button>
                      </div>
                    </Card>
                  </div>
                )}
              </div>
            )}

            {/* Offers Tab */}
            {activeTab === "offers" && (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Banknote className="w-5 h-5 text-emerald-600" />
                          Offers
                        </CardTitle>
                        <CardDescription>Manage offers for this property</CardDescription>
                      </div>
                      <Button onClick={() => setShowAddOfferModal(true)} data-testid="add-offer-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Offer
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="bg-slate-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-slate-800">{offersStats.total || 0}</p>
                        <p className="text-xs text-slate-500">Total Offers</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-amber-600">{offersStats.pending || 0}</p>
                        <p className="text-xs text-amber-600">Pending</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-emerald-600">
                          {offersStats.highest_offer ? `£${offersStats.highest_offer.toLocaleString()}` : "—"}
                        </p>
                        <p className="text-xs text-emerald-600">Highest Offer</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-blue-600">
                          {offersStats.asking_price ? `£${offersStats.asking_price.toLocaleString()}` : "—"}
                        </p>
                        <p className="text-xs text-blue-600">Asking Price</p>
                      </div>
                    </div>

                    {/* Offers List */}
                    {offers.length === 0 ? (
                      <div className="text-center py-12 bg-slate-50 rounded-lg">
                        <Banknote className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No offers received yet</p>
                        <Button 
                          variant="outline" 
                          className="mt-4"
                          onClick={() => setShowAddOfferModal(true)}
                        >
                          Record First Offer
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {offers.map((offer) => (
                          <div 
                            key={offer.id} 
                            className={`border rounded-lg p-4 hover:shadow-sm transition-shadow ${
                              offer.status === "accepted" ? "border-emerald-300 bg-emerald-50/50" :
                              offer.status === "rejected" ? "border-red-200 bg-red-50/30" :
                              "border-slate-200"
                            }`}
                            data-testid={`offer-${offer.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <p className="text-xl font-bold text-slate-800">
                                    £{offer.amount?.toLocaleString()}
                                  </p>
                                  <Badge className={
                                    offer.status === "accepted" ? "bg-emerald-100 text-emerald-700" :
                                    offer.status === "rejected" ? "bg-red-100 text-red-700" :
                                    offer.status === "pending" ? "bg-amber-100 text-amber-700" :
                                    offer.status === "counter" ? "bg-purple-100 text-purple-700" :
                                    "bg-slate-100 text-slate-700"
                                  }>
                                    {offer.status}
                                  </Badge>
                                  {offersStats.asking_price && (
                                    <span className="text-sm text-slate-500">
                                      ({((offer.amount / offersStats.asking_price) * 100).toFixed(1)}% of asking)
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 text-sm text-slate-600 mb-2">
                                  <span className="flex items-center gap-1">
                                    <User className="w-4 h-4" />
                                    {offer.buyer_name}
                                  </span>
                                  <Badge variant="outline">{offer.buyer_position}</Badge>
                                  {offer.mortgage_in_principle && (
                                    <Badge className="bg-blue-100 text-blue-700">MIP</Badge>
                                  )}
                                  {offer.proof_of_funds && (
                                    <Badge className="bg-green-100 text-green-700">POF</Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 text-sm text-slate-500">
                                  {offer.buyer_email && (
                                    <span className="flex items-center gap-1">
                                      <Mail className="w-4 h-4" />
                                      {offer.buyer_email}
                                    </span>
                                  )}
                                  {offer.buyer_phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-4 h-4" />
                                      {offer.buyer_phone}
                                    </span>
                                  )}
                                </div>
                                {offer.conditions && (
                                  <p className="text-sm text-slate-600 mt-2">
                                    <strong>Conditions:</strong> {offer.conditions}
                                  </p>
                                )}
                                {offer.notes && (
                                  <p className="text-sm text-slate-500 mt-1 italic">"{offer.notes}"</p>
                                )}
                                {offer.seller_response && (
                                  <div className="mt-2 p-2 bg-slate-100 rounded text-sm">
                                    <strong>Seller Response:</strong> {offer.seller_response}
                                  </div>
                                )}
                                <p className="text-xs text-slate-400 mt-2">
                                  Received: {new Date(offer.created_at).toLocaleDateString('en-GB', {
                                    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                  })}
                                </p>
                              </div>
                              
                              {/* Actions */}
                              {offer.status === "pending" && (
                                <div className="flex items-center gap-2 ml-4">
                                  <Button 
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    onClick={() => handleUpdateOfferStatus(offer.id, "accepted")}
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    Accept
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => handleUpdateOfferStatus(offer.id, "rejected")}
                                  >
                                    <X className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Add Offer Modal */}
                {showAddOfferModal && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <Card className="w-full max-w-lg">
                      <CardHeader>
                        <CardTitle>Record New Offer</CardTitle>
                        <CardDescription>Add an offer received for this property</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Offer Amount (£) *</Label>
                            <Input
                              type="number"
                              value={newOffer.amount}
                              onChange={(e) => setNewOffer({...newOffer, amount: e.target.value})}
                              placeholder="500000"
                            />
                          </div>
                          <div>
                            <Label>Buyer Position</Label>
                            <Select 
                              value={newOffer.buyer_position} 
                              onValueChange={(val) => setNewOffer({...newOffer, buyer_position: val})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="First Time Buyer">First Time Buyer</SelectItem>
                                <SelectItem value="Chain Free">Chain Free</SelectItem>
                                <SelectItem value="Has Chain">Has Chain</SelectItem>
                                <SelectItem value="Cash Buyer">Cash Buyer</SelectItem>
                                <SelectItem value="Investor">Investor</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label>Buyer Name *</Label>
                          <Input
                            value={newOffer.buyer_name}
                            onChange={(e) => setNewOffer({...newOffer, buyer_name: e.target.value})}
                            placeholder="John Smith"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Email</Label>
                            <Input
                              type="email"
                              value={newOffer.buyer_email}
                              onChange={(e) => setNewOffer({...newOffer, buyer_email: e.target.value})}
                              placeholder="john@example.com"
                            />
                          </div>
                          <div>
                            <Label>Phone</Label>
                            <Input
                              value={newOffer.buyer_phone}
                              onChange={(e) => setNewOffer({...newOffer, buyer_phone: e.target.value})}
                              placeholder="07123456789"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newOffer.mortgage_in_principle}
                              onChange={(e) => setNewOffer({...newOffer, mortgage_in_principle: e.target.checked})}
                              className="rounded"
                            />
                            <span className="text-sm">Mortgage in Principle</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newOffer.proof_of_funds}
                              onChange={(e) => setNewOffer({...newOffer, proof_of_funds: e.target.checked})}
                              className="rounded"
                            />
                            <span className="text-sm">Proof of Funds</span>
                          </label>
                        </div>
                        <div>
                          <Label>Conditions</Label>
                          <Input
                            value={newOffer.conditions}
                            onChange={(e) => setNewOffer({...newOffer, conditions: e.target.value})}
                            placeholder="Subject to survey, completion date..."
                          />
                        </div>
                        <div>
                          <Label>Notes</Label>
                          <Textarea
                            value={newOffer.notes}
                            onChange={(e) => setNewOffer({...newOffer, notes: e.target.value})}
                            placeholder="Additional notes..."
                            rows={2}
                          />
                        </div>
                      </CardContent>
                      <div className="flex justify-end gap-2 p-4 border-t">
                        <Button variant="outline" onClick={() => setShowAddOfferModal(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleCreateOffer} disabled={savingOffer}>
                          {savingOffer ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Submit Offer
                        </Button>
                      </div>
                    </Card>
                  </div>
                )}
              </div>
            )}

            {/* Contract Tab */}
            {activeTab === "contract" && (
              <div className="space-y-4">
                {/* Contract Terms - Editable before sending */}
                {(!contract?.status || contract?.status === "NOT_SENT") && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <PoundSterling className="w-5 h-5" />
                        Contract Terms
                      </CardTitle>
                      <CardDescription>Set the fee and contract period before sending</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <Label htmlFor="agency_type">Agency Type</Label>
                          <Select 
                            value={instruction?.agency_type || "sole"}
                            onValueChange={async (value) => {
                              try {
                                await axios.patch(
                                  `${API}/sales-instructions/instructions/${instructionId}`,
                                  { agency_type: value },
                                  { headers: { Authorization: `Bearer ${token}` }}
                                );
                                setInstruction({...instruction, agency_type: value});
                                toast.success("Agency type updated");
                              } catch (err) {
                                toast.error("Failed to update agency type");
                              }
                            }}
                          >
                            <SelectTrigger id="agency_type" data-testid="agency-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sole">Sole Agency</SelectItem>
                              <SelectItem value="joint_sole">Joint Sole Agency</SelectItem>
                              <SelectItem value="multi">Multi Agency</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label htmlFor="fee_percentage">Fee Percentage (%)</Label>
                          <Input
                            id="fee_percentage"
                            type="number"
                            step="0.05"
                            min="0"
                            max="5"
                            value={instruction?.fee_percentage || 1.25}
                            onChange={async (e) => {
                              const value = parseFloat(e.target.value);
                              if (value >= 0 && value <= 5) {
                                try {
                                  await axios.patch(
                                    `${API}/sales-instructions/instructions/${instructionId}`,
                                    { fee_percentage: value, fee_fixed: null },
                                    { headers: { Authorization: `Bearer ${token}` }}
                                  );
                                  setInstruction({...instruction, fee_percentage: value, fee_fixed: null});
                                  toast.success("Fee updated");
                                } catch (err) {
                                  toast.error("Failed to update fee");
                                }
                              }
                            }}
                            data-testid="fee-percentage-input"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="fee_fixed">Or Fixed Fee (£)</Label>
                          <Input
                            id="fee_fixed"
                            type="number"
                            step="100"
                            min="0"
                            placeholder="Leave blank for %"
                            value={instruction?.fee_fixed || ""}
                            onChange={async (e) => {
                              const value = e.target.value ? parseFloat(e.target.value) : null;
                              try {
                                await axios.patch(
                                  `${API}/sales-instructions/instructions/${instructionId}`,
                                  { fee_fixed: value },
                                  { headers: { Authorization: `Bearer ${token}` }}
                                );
                                setInstruction({...instruction, fee_fixed: value});
                                toast.success("Fixed fee updated");
                              } catch (err) {
                                toast.error("Failed to update fixed fee");
                              }
                            }}
                            data-testid="fee-fixed-input"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="contract_term_weeks">Contract Term (weeks)</Label>
                          <Input
                            id="contract_term_weeks"
                            type="number"
                            min="4"
                            max="52"
                            value={instruction?.contract_term_weeks || 12}
                            onChange={async (e) => {
                              const value = parseInt(e.target.value);
                              if (value >= 4 && value <= 52) {
                                try {
                                  await axios.patch(
                                    `${API}/sales-instructions/instructions/${instructionId}`,
                                    { contract_term_weeks: value },
                                    { headers: { Authorization: `Bearer ${token}` }}
                                  );
                                  setInstruction({...instruction, contract_term_weeks: value});
                                  toast.success("Contract term updated");
                                } catch (err) {
                                  toast.error("Failed to update contract term");
                                }
                              }
                            }}
                            data-testid="contract-term-input"
                          />
                        </div>
                      </div>
                      
                      {/* Fee Preview */}
                      {instruction?.price_amount && (
                        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-600">
                            <strong>Fee Preview:</strong>{" "}
                            {instruction?.fee_fixed ? (
                              <>£{instruction.fee_fixed.toLocaleString()} + VAT = £{(instruction.fee_fixed * 1.2).toLocaleString()} total (fixed)</>
                            ) : (
                              <>
                                {instruction?.fee_percentage || 1.25}% of £{instruction.price_amount.toLocaleString()} = 
                                £{((instruction.price_amount * (instruction?.fee_percentage || 1.25)) / 100).toLocaleString()} + VAT = 
                                £{(((instruction.price_amount * (instruction?.fee_percentage || 1.25)) / 100) * 1.2).toLocaleString()} total
                              </>
                            )}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle>Contract Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 mb-6">
                      <Badge className={`${contractStatusColors[contract?.status]} text-lg px-4 py-2`}>
                        {contract?.status || "NOT_SENT"}
                      </Badge>
                      {contract?.sent_at && (
                        <span className="text-sm text-slate-500">
                          Sent: {new Date(contract.sent_at).toLocaleDateString()}
                        </span>
                      )}
                      {contract?.signed_at && (
                        <span className="text-sm text-green-600">
                          Signed: {new Date(contract.signed_at).toLocaleDateString()}
                        </span>
                      )}
                      {contract?.docusign_envelope_id && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                          DocuSign
                        </Badge>
                      )}
                    </div>

                    <div className="flex gap-3 flex-wrap">
                      {/* Preview Contract Button - Always available for staff review */}
                      <Button 
                        variant="outline" 
                        onClick={async () => {
                          try {
                            const response = await axios.post(
                              `${API}/sales-instructions/instructions/${instructionId}/contract/generate-preview`,
                              {},
                              { 
                                headers: { Authorization: `Bearer ${token}` },
                                responseType: 'blob'
                              }
                            );
                            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `Contract_Preview_${instructionId}.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                            toast.success("Contract preview downloaded");
                          } catch (error) {
                            toast.error("Failed to generate contract preview");
                          }
                        }}
                        data-testid="preview-contract-btn"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Preview Contract
                      </Button>
                      
                      {/* DocuSign Send Button - show for NOT_SENT or VOIDED */}
                      {(contract?.status === "NOT_SENT" || contract?.status === "VOIDED") && docusignAvailable && (
                        <Button 
                          onClick={initDocusignSigners}
                          className="bg-purple-600 hover:bg-purple-700"
                          data-testid="send-docusign-btn"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {contract?.status === "VOIDED" ? "Resend via DocuSign" : "Send via DocuSign"}
                        </Button>
                      )}
                      
                      {/* Manual Send (fallback if DocuSign not available) */}
                      {(contract?.status === "NOT_SENT" || contract?.status === "VOIDED") && !docusignAvailable && (
                        <Button onClick={handleSendContract} data-testid="send-contract-btn">
                          <Send className="w-4 h-4 mr-2" />
                          Send Contract
                        </Button>
                      )}
                      
                      {/* DocuSign Actions for sent contracts */}
                      {contract?.docusign_envelope_id && contract?.status === "SENT" && (
                        <>
                          <Button variant="outline" onClick={handleResendDocuSign}>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Resend Notifications
                          </Button>
                          <Button variant="destructive" size="sm" onClick={handleVoidDocuSign}>
                            Void Contract
                          </Button>
                        </>
                      )}
                      
                      {/* Manual upload for non-DocuSign contracts */}
                      {contract?.status !== "SIGNED" && !contract?.docusign_envelope_id && (
                        <>
                          <input
                            type="file"
                            ref={contractInputRef}
                            className="hidden"
                            accept=".pdf"
                            onChange={(e) => {
                              if (e.target.files[0]) handleUploadSignedContract(e.target.files[0]);
                            }}
                          />
                          <Button variant="outline" onClick={() => contractInputRef.current?.click()} data-testid="upload-signed-contract-btn">
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Signed Contract
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* DocuSign Signers Status */}
                {contract?.docusign_envelope_id && contract?.signers && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Signing Progress
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {contract.signers.map((signer, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                signer.signed ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'
                              }`}>
                                {signer.routing_order}
                              </div>
                              <div>
                                <div className="font-medium">{signer.name}</div>
                                <div className="text-sm text-slate-500">{signer.email}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={signer.type === 'staff' ? 'default' : 'secondary'}>
                                {signer.type === 'staff' ? 'Staff' : 'Seller'}
                              </Badge>
                              {signer.signed ? (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Signed
                                </Badge>
                              ) : (
                                <Badge variant="outline">Pending</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-slate-500 mt-3">
                        Staff signs first (Order 1), then all sellers sign (Order 2).
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Signed Contract Document */}
                {contract?.status === "SIGNED" && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-green-600 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        Contract Signed
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {documents.filter(d => d.document_type === "SIGNED_CONTRACT" || d.category === "CONTRACT_SIGNED").map((doc) => (
                        <div key={doc.id} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                          <FileText className="w-5 h-5 text-green-600" />
                          <div className="flex-1">
                            <div className="font-medium">{doc.filename || doc.title}</div>
                            <div className="text-sm text-slate-500">
                              {new Date(doc.uploaded_at || doc.created_at).toLocaleDateString()}
                              {doc.docusign_envelope_id && " • via DocuSign"}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" asChild>
                            <a href={`${API.replace("/api", "")}${doc.file_url}`} target="_blank" rel="noopener">
                              <Eye className="w-4 h-4" />
                            </a>
                          </Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                
                {/* DocuSign Send Modal */}
                {showDocusignModal && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <Card className="w-full max-w-lg mx-4">
                      <CardHeader>
                        <CardTitle>Send Contract via DocuSign</CardTitle>
                        <p className="text-sm text-slate-500">
                          You (staff) will sign first, then sellers will receive the contract to sign.
                        </p>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Seller Signers</label>
                          {docusignSigners.map((signer, idx) => (
                            <div key={idx} className="flex gap-2 mb-2">
                              <Input
                                placeholder="Name"
                                value={signer.name}
                                onChange={(e) => updateDocusignSigner(idx, 'name', e.target.value)}
                                className="flex-1"
                              />
                              <Input
                                placeholder="Email"
                                type="email"
                                value={signer.email}
                                onChange={(e) => updateDocusignSigner(idx, 'email', e.target.value)}
                                className="flex-1"
                              />
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => removeDocusignSigner(idx)}
                                disabled={docusignSigners.length === 1}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          <Button variant="outline" size="sm" onClick={addDocusignSigner} className="mt-2">
                            <Plus className="w-4 h-4 mr-1" />
                            Add Signer
                          </Button>
                        </div>
                        
                        <div className="bg-purple-50 p-3 rounded-lg text-sm">
                          <p className="font-medium text-purple-800">Signing Order:</p>
                          <ol className="list-decimal list-inside text-purple-700 mt-1">
                            <li>You (Staff) - signs first</li>
                            <li>Sellers - sign after staff completes</li>
                          </ol>
                        </div>
                      </CardContent>
                      <div className="flex justify-end gap-2 p-4 border-t">
                        <Button variant="outline" onClick={() => setShowDocusignModal(false)}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleSendDocuSign} 
                          disabled={sendingDocusign || docusignSigners.some(s => !s.name || !s.email)}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          {sendingDocusign ? "Sending..." : "Send for Signing"}
                        </Button>
                      </div>
                    </Card>
                  </div>
                )}
              </div>
            )}

            {/* Marketing Tab */}
            {activeTab === "marketing" && (
              <div className="space-y-6">
                {/* Summary - 300 characters */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Property Summary</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">Short summary for listings (max 300 characters)</p>
                    </div>
                    <Button
                      variant={editMode ? "default" : "outline"}
                      size="sm"
                      onClick={() => editMode ? handleSave() : setEditMode(true)}
                      disabled={saving}
                    >
                      {editMode ? "Save All" : "Edit"}
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {editMode ? (
                      <div>
                        <Textarea
                          value={formData.summary || ""}
                          onChange={(e) => {
                            if (e.target.value.length <= 300) {
                              setFormData({...formData, summary: e.target.value});
                            }
                          }}
                          rows={3}
                          placeholder="Brief property summary for portal listings..."
                          maxLength={300}
                        />
                        <p className="text-xs text-slate-500 mt-1 text-right">
                          {(formData.summary || "").length}/300 characters
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm">
                        {instruction.summary || "No summary added yet."}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Key Features - up to 10 */}
                <Card>
                  <CardHeader>
                    <CardTitle>Key Features</CardTitle>
                    <p className="text-sm text-slate-500">Up to 10 bullet points highlighting the property</p>
                  </CardHeader>
                  <CardContent>
                    {editMode ? (
                      <div className="space-y-2">
                        {(formData.key_features || []).map((feature, idx) => (
                          <div key={idx} className="flex gap-2">
                            <span className="text-slate-400 mt-2">•</span>
                            <Input
                              value={feature}
                              onChange={(e) => {
                                const updated = [...(formData.key_features || [])];
                                updated[idx] = e.target.value;
                                setFormData({...formData, key_features: updated});
                              }}
                              placeholder={`Feature ${idx + 1}`}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const updated = (formData.key_features || []).filter((_, i) => i !== idx);
                                setFormData({...formData, key_features: updated});
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        {(formData.key_features || []).length < 10 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                key_features: [...(formData.key_features || []), ""]
                              });
                            }}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Feature
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div>
                        {(instruction.key_features || []).length > 0 ? (
                          <ul className="list-disc list-inside space-y-1">
                            {instruction.key_features.map((feature, idx) => (
                              <li key={idx} className="text-sm">{feature}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-slate-500">No key features added yet.</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Long Description */}
                <Card>
                  <CardHeader>
                    <CardTitle>Full Description</CardTitle>
                    <p className="text-sm text-slate-500">Detailed property description for brochures and portals</p>
                  </CardHeader>
                  <CardContent>
                    {editMode ? (
                      <Textarea
                        value={formData.marketing_description || ""}
                        onChange={(e) => setFormData({...formData, marketing_description: e.target.value})}
                        rows={12}
                        placeholder="Enter detailed property description..."
                      />
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">
                        {instruction.marketing_description || "No description added yet."}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Photos Tab */}
            {activeTab === "photos" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Photos ({photos.length}/20)</CardTitle>
                  <div>
                    <input
                      type="file"
                      ref={photoInputRef}
                      className="hidden"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length > 0) {
                          handleBulkMediaUpload(files, "PHOTO");
                        }
                        e.target.value = ""; // Reset input
                      }}
                    />
                    <Button onClick={() => photoInputRef.current?.click()} disabled={uploading}>
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload Photos (up to 20)
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {photos.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Image className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No photos uploaded yet</p>
                      <p className="text-sm mt-1">Select up to 20 photos at once</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 gap-4">
                      {photos.map((photo) => (
                        <div key={photo.id} className="relative group">
                          <img
                            src={`${API.replace("/api", "")}${photo.file_url}`}
                            alt={photo.caption || "Photo"}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          {photo.type === "MAIN_IMAGE" && (
                            <Badge className="absolute top-2 left-2 bg-blue-600">Main</Badge>
                          )}
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7"
                            onClick={() => handleDeleteMedia(photo.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Floorplan Tab */}
            {activeTab === "floorplan" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Floorplan</CardTitle>
                  <div>
                    <input
                      type="file"
                      ref={floorplanInputRef}
                      className="hidden"
                      accept="image/*,.pdf"
                      onChange={(e) => {
                        if (e.target.files[0]) handleMediaUpload(e.target.files[0], "FLOORPLAN");
                      }}
                    />
                    <Button onClick={() => floorplanInputRef.current?.click()} disabled={uploading}>
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {floorplan ? "Replace" : "Upload"} Floorplan
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {!floorplan ? (
                    <div className="text-center py-12 text-slate-500">
                      <Layout className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No floorplan uploaded yet</p>
                    </div>
                  ) : (
                    <div className="relative">
                      <img
                        src={`${API.replace("/api", "")}${floorplan.file_url}`}
                        alt="Floorplan"
                        className="max-w-full rounded-lg"
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-4 right-4"
                        onClick={() => handleDeleteMedia(floorplan.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* EPC Tab */}
            {activeTab === "epc" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>EPC Certificate</CardTitle>
                  <div>
                    <input
                      type="file"
                      ref={epcInputRef}
                      className="hidden"
                      accept="image/*,.pdf"
                      onChange={(e) => {
                        if (e.target.files[0]) handleMediaUpload(e.target.files[0], "EPC");
                      }}
                    />
                    <Button onClick={() => epcInputRef.current?.click()} disabled={uploading}>
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {epc ? "Replace" : "Upload"} EPC
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <Label>EPC Rating</Label>
                    <p className="text-2xl font-bold text-green-600">{instruction.epc_rating || "—"}</p>
                  </div>
                  {!epc ? (
                    <div className="text-center py-8 text-slate-500 border-t">
                      <Zap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No EPC certificate uploaded</p>
                    </div>
                  ) : (
                    <div className="relative border-t pt-4">
                      <img
                        src={`${API.replace("/api", "")}${epc.file_url}`}
                        alt="EPC"
                        className="max-w-full rounded-lg"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Documents Tab */}
            {activeTab === "documents" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Documents Vault</CardTitle>
                  <div className="flex gap-2">
                    <Select value={documentCategory} onValueChange={setDocumentCategory}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SELLER_ID">Seller ID</SelectItem>
                        <SelectItem value="LEASE">Lease</SelectItem>
                        <SelectItem value="MANAGEMENT_INFO">Management Info</SelectItem>
                        <SelectItem value="SERVICE_CHARGE">Service Charge</SelectItem>
                        <SelectItem value="GROUND_RENT">Ground Rent</SelectItem>
                        <SelectItem value="EPC_DOC">EPC Document</SelectItem>
                        <SelectItem value="PROPERTY_FORMS">Property Forms</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <input
                      type="file"
                      ref={documentInputRef}
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files[0]) handleDocumentUpload(e.target.files[0]);
                      }}
                    />
                    <Button onClick={() => documentInputRef.current?.click()} disabled={uploading}>
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {documents.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <FolderOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No documents uploaded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {documents.map((doc) => (
                        <div key={doc.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <FileText className="w-5 h-5 text-slate-500" />
                          <div className="flex-1">
                            <div className="font-medium">{doc.title}</div>
                            <div className="text-xs text-slate-500">
                              {doc.category.replace(/_/g, " ")} • {new Date(doc.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" asChild>
                            <a href={`${API.replace("/api", "")}${doc.file_url}`} target="_blank" rel="noopener">
                              <Eye className="w-4 h-4" />
                            </a>
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Brochure Tab */}
            {activeTab === "brochure" && (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Brochure Generation</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 mb-6">
                      <Badge className={`${brochureStatusColors[brochure?.status] || "bg-slate-100"} text-lg px-4 py-2`}>
                        {brochure?.status || "Not Generated"}
                      </Badge>
                      {brochure?.generated_at && (
                        <span className="text-sm text-slate-500">
                          Generated: {new Date(brochure.generated_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    <div className="flex gap-3">
                      <Button onClick={handleGenerateBrochure} disabled={uploading}>
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                        {brochure ? "Regenerate" : "Generate"} Brochure
                      </Button>
                      
                      {brochure && (
                        <>
                          <Button variant="outline" asChild>
                            <a href={`${API.replace("/api", "")}${brochure.pdf_file_url}`} target="_blank" rel="noopener">
                              <Eye className="w-4 h-4 mr-2" />
                              Preview
                            </a>
                          </Button>
                          
                          {brochure.status === "GENERATED" && (
                            <Button variant="outline" onClick={() => handleBrochureAction("send")}>
                              <Send className="w-4 h-4 mr-2" />
                              Send to Seller
                            </Button>
                          )}
                          
                          {!["APPROVED", "OVERRIDE_APPROVED"].includes(brochure.status) && (
                            <Button onClick={() => handleBrochureAction("approve")}>
                              <Check className="w-4 h-4 mr-2" />
                              Approve (Override)
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {brochure?.seller_notes && (
                  <Card className="border-orange-200 bg-orange-50">
                    <CardHeader>
                      <CardTitle className="text-orange-700">Seller Notes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm">{brochure.seller_notes}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Publish Tab */}
            {activeTab === "publish" && (
              <Card>
                <CardHeader>
                  <CardTitle>Publish to Portals</CardTitle>
                  <CardDescription>
                    Publish this property to Rightmove, Zoopla, and OnTheMarket
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!readiness?.is_ready ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                      <div className="font-medium text-amber-800 mb-2">Not Ready to Publish</div>
                      <ul className="text-sm text-amber-700 space-y-1">
                        {readiness?.errors?.map((err, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <XCircle className="w-4 h-4" />
                            {err}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                      <div className="font-medium text-green-800 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        Ready to Publish
                      </div>
                    </div>
                  )}

                  <div className="space-y-3 mb-6">
                    {["RIGHTMOVE", "ZOOPLA", "ONTHEMARKET"].map((portal) => (
                      <div key={portal} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <Globe className="w-5 h-5 text-slate-500" />
                        <span className="font-medium">{portal}</span>
                        <Badge variant="outline" className="ml-auto">
                          {instruction.portal_listing_enabled ? "Queued" : "Not Published"}
                        </Badge>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={handlePublish}
                    disabled={!readiness?.is_ready}
                    className="w-full"
                    size="lg"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Publish to All Portals
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SIPropertyOverviewPage;
